"use client";

import Dropdown from "@/components/Dropdown";
import { useEffect, useState } from "react";
import apiFetch from "@/lib/fetch";
import { useCampaignContext } from "@/context/CampaignContext";
import { useTranslation } from "@/hooks/translationContext";

export default function CampaignFilters(props: SearchProps) {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { setCampaignFilters } = useCampaignContext();

  const handleServiceReset = () => {
    setSelectedService(null);
  };

  const handleServiceSelect = (service) => {
    setSelectedService({
      title: service.title,
      id: service.id,
    });
  };

  const handleDistrictReset = () => {
    setSelectedDistrict(null);
  };

  const handleDistrictSelect = (district) => {
    setSelectedDistrict({
      title: district.title,
      id: district.id,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const commonParams = new URLSearchParams();
        if (props.service) commonParams.append("service", props.service);
        if (props.district) commonParams.append("district", props.district);

        const servicesParams = new URLSearchParams(commonParams);
        if (selectedDistrict?.id)
          servicesParams.append("campaignDistrict", selectedDistrict.id);

        const districtParams = new URLSearchParams(commonParams);
        if (selectedService?.id)
          districtParams.append("campaignService", selectedService.id);

        const [servicesData, districtsData] = await Promise.all([
          apiFetch({ route: `campaign/services?${servicesParams}` }),
          apiFetch({ route: `campaign/districts?${districtParams}` }),
        ]);

        setServices(servicesData || []);
        setDistricts(districtsData || []);
      } catch (err) {
        setServices([]);
        setDistricts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [props.service, props.district, selectedDistrict, selectedService]);

  useEffect(() => {
    setCampaignFilters({
      service: selectedService,
      district: selectedDistrict,
    });
  }, [selectedDistrict, selectedService, setCampaignFilters]);

  const isServicesEmpty = !isLoading && services.length === 0;
  const isDistrictsEmpty = !isLoading && districts.length === 0;

  return (
    <div className="flex md:justify-end md:flex-wrap gap-4 mt-4">
      <Dropdown
        parentClassName="lg:!w-[278px] w-full"
        title={selectedDistrict?.title || t("campaign-filter-district-placeholder")}
        className={`w-full !z-40 ${isDistrictsEmpty ? "opacity-50 cursor-not-allowed" : ""}`}
        dropdownClassName="!z-30"
        disabled={isDistrictsEmpty}
      >
        {isLoading ? (
          <div className="p-3 text-center text-sm text-slate-500">
            {t("common-loading")}
          </div>
        ) : isDistrictsEmpty ? (
          <div className="p-3 text-center text-sm text-slate-500">
            {t("company-discount-district-not-found")}
          </div>
        ) : (
          <>
            <button
              role="button"
              className="flex items-start w-full px-2 border-b border-stone-50 py-3 rounded cursor-pointer"
              onClick={handleDistrictReset}
            >
              <span className="text-sm text-slate-800 font-bold">Tümü</span>
            </button>

            {districts.map((district) => (
              <button
                role="button"
                key={district.id}
                onClick={() => handleDistrictSelect(district)}
                className="w-full flex items-start px-2 border-b border-stone-50 font-bold py-3 rounded cursor-pointer"
              >
                <span className="text-sm text-slate-800 font-bold">
                  {district?.title}
                </span>
              </button>
            ))}
          </>
        )}
      </Dropdown>

      <Dropdown
        parentClassName="lg:!w-[278px] w-full"
        title={selectedService?.title || t("campaign-filter-service-placeholder")}
        className={`w-full !z-40 ${isServicesEmpty ? "opacity-50 cursor-not-allowed" : ""}`}
        dropdownClassName="!z-30"
        disabled={isServicesEmpty}
      >
        {isLoading ? (
          <div className="p-3 text-center text-sm text-slate-500">
            {t("common-loading")}
          </div>
        ) : isServicesEmpty ? (
          <div className="p-3 text-center text-sm text-slate-500">
            {t("company-discount-service-not-found")}
          </div>
        ) : (
          <>
            <button
              role="button"
              className="flex items-start w-full px-2 border-b border-stone-50 py-3 rounded cursor-pointer"
              onClick={handleServiceReset}
            >
              <span className="text-sm text-slate-800 font-bold">Tümü</span>
            </button>

            {services.map((service) => (
              <button
                role="button"
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                className="w-full flex items-start px-2 border-b border-stone-50 font-bold py-3 rounded cursor-pointer"
              >
                <span className="text-sm text-slate-800 font-bold">
                  {service?.title}
                </span>
              </button>
            ))}
          </>
        )}
      </Dropdown>
    </div>
  );
}
